import { ColumnConfig } from '@/components/table/Table';
import { ShippingApi, ShippingZoneApi, cleanShippingDetails } from '../models';
import { formatDate, extractDate } from '@/utils/dates';
import { formatTimeSlotLabel } from '@/utils/timeSlot';
import { isPickupServiceVisit } from '@/pages/Business/pages/LogisticOperator/models/ServiceWithOperator';
import { isMissingCorreoLabelNumber } from '../utils/correoLabel';
import {
  COUNTRY,
  COUNTRY_LABEL,
  OTHER_COUNTRY,
  wallClockInCountry,
} from '@/config/country';

function getAiCategoryLabel(shipping: ShippingApi): string | null {
  if (shipping.productCategory?.trim()) return shipping.productCategory.trim();
  const fromProducts = shipping.shippingProducts
    ?.map((p) => p.category?.trim())
    .filter((c): c is string => Boolean(c));
  if (!fromProducts?.length) return null;
  return [...new Set(fromProducts)].join(', ');
}

function getAiProductSizeLabel(shipping: ShippingApi): string | null {
  const sizes = shipping.shippingProducts
    ?.map((p) => p.productSize?.name?.trim())
    .filter((s): s is string => Boolean(s)) ?? [];
  if (!sizes.length) return null;
  const unique = [...new Set(sizes)];
  return unique.join(', ');
}

const RISK_CONFIG = {
  GREEN:  { label: 'Bajo',  bg: 'bg-green-100',  text: 'text-green-700' },
  YELLOW: { label: 'Medio', bg: 'bg-yellow-100', text: 'text-yellow-700' },
  RED:    { label: 'Alto',  bg: 'bg-red-100',    text: 'text-red-700' },
} as const;

// Mantener sincronizado con business/src/constants/integrationSystem.ts
export const INTEGRATION_SYSTEM_CODES = [
  'SD_API_V2',
  'SD_WOOCOMMERCE_CONNECTOR',
  'SD_SHOPIFY_CONNECTOR_V1',
  'SD_SHOPIFY_CONNECTOR_V2',
  'SD_MELI_INTEGRATION_V2',
] as const;

export type IntegrationSystemCode = (typeof INTEGRATION_SYSTEM_CODES)[number];

export const INTEGRATION_SYSTEM_LABELS: Record<IntegrationSystemCode, string> = {
  SD_API_V2: 'SD API v2',
  SD_WOOCOMMERCE_CONNECTOR: 'WooCommerce',
  SD_SHOPIFY_CONNECTOR_V1: 'Shopify v1',
  SD_SHOPIFY_CONNECTOR_V2: 'Shopify v2',
  SD_MELI_INTEGRATION_V2: 'Mercado Libre v2',
};

export function getIntegrationSystemLabel(code: string | null | undefined): string {
  const trimmed = code?.trim();
  if (!trimmed) return INTEGRATION_SYSTEM_LABELS.SD_API_V2;
  return INTEGRATION_SYSTEM_LABELS[trimmed as IntegrationSystemCode] ?? trimmed;
}

const ZONE_TYPE_NAME_RATE = 'rateZone';
const ZONE_TYPE_NAME_ROUTING = 'routingZone';

function isRateShippingZone(shippingZone: ShippingZoneApi): boolean {
  return shippingZone.zoneType?.name?.trim() === ZONE_TYPE_NAME_RATE;
}

function isRoutingShippingZone(shippingZone: ShippingZoneApi): boolean {
  return shippingZone.zoneType?.name?.trim() === ZONE_TYPE_NAME_ROUTING;
}

function getTimeSlotDisplayLabel(shipping: ShippingApi): string {
  const slot = shipping.timeSlot;
  if (!slot) {
    return shipping.timeSlotId || 'N/A';
  }

  return formatTimeSlotLabel(
    { code: slot.code, from: slot.dateTimeFrom, to: slot.dateTimeTo },
    'N/A'
  );
}
export const shippingProductsColumns: ColumnConfig[] = [
  {
    label: 'Cantidad',
    field: 'quantity',
    render: (value: number) => value || 0,
  },
  {
    label: 'Código',
    field: 'code',
    render: (value: string) => value || 'N/A',
  },
  {
    label: 'SKU',
    field: 'sku',
    render: (value: string) => value || 'N/A',
  },
  {
    label: 'Nombre',
    field: 'name',
    render: (value: string) => value || 'N/A',
  },
  {
    label: 'Tamaño Sugerido',
    field: 'productSize',
    render: (value: any) => value?.name || '—',
  },
  {
    label: 'Categoría Sugerida',
    field: 'category',
    render: (value: string) => value || '—',
  },
  {
    label: 'Peso',
    field: 'weight',
    render: (value: string) => (value ? `${value} kg` : 'N/A'),
  },
  {
    label: 'Observación',
    field: 'description',
    render: (value: string) => value || 'N/A',
  },
];

export const shippingPackagesColumns: ColumnConfig[] = [
  {
    label: 'Código',
    field: 'code',
    render: (value: string) => value || 'N/A',
  },
  {
    label: 'Descripción corta',
    field: 'shortDescription',
    render: (value: string) => value || 'N/A',
  },
  {
    label: 'Descripción',
    field: 'description',
    render: (value: string) =>
      value ? (
        <span className='block max-w-[420px] truncate'>{value}</span>
      ) : (
        '—'
      ),
  },
  {
    label: 'Peso',
    field: 'weight',
    render: (value: number) => (value ? `${value} kg` : '—'),
  },
  {
    label: 'Agregado por',
    field: 'createdBy',
    render: (value: string) => value || 'N/A',
  },
  {
    label: 'Fecha Ingreso',
    field: 'createdAt',
    render: (value: string) => (value ? formatDate(value) : 'N/A'),
  },
];

const getFullAddress = (address?: any) => {
  if (!address) return 'N/A';
  const parts = [
    address.streetName,
    address.doorNumber,
    address.apartmentNumber,
  ].filter(Boolean);
  return parts.join(' ') || 'N/A';
};

export const getShippingDetailsFirstTableColumns = (
  shipping: ShippingApi,
  handleOpenDestinationMap: () => void
): ColumnConfig[] => [
  {
    label: 'ID Externo',
    field: 'externalId',
    render: () => (
      <span className='text-sm'>
        {shipping.commerceShippingNumber || 'N/A'}
      </span>
    ),
  },
  {
    label: 'Operador logístico',
    field: 'headquarterLogisticOperator',
    render: () => {
      if (!shipping.logisticOperator) {
        return <span className='text-sm'>N/A</span>;
      }

      const isSubOperator = shipping.logisticOperator.headquarter === false;
      const hasParent = shipping.logisticOperator.parent;

      if (isSubOperator && hasParent) {
        return <span className='text-sm'>{hasParent.name}</span>;
      }

      return <span className='text-sm'>{shipping.logisticOperator.name}</span>;
    },
  },
  {
    label: 'Comercio',
    field: 'business',
    render: () => (
      <span className='text-sm'>{shipping.commerce?.name || 'N/A'}</span>
    ),
  },
  {
    label: 'Nombre del cliente',
    field: 'recipient',
    render: () => (
      <span className='text-sm'>{shipping.recipient || 'N/A'}</span>
    ),
  },
  {
    label: 'Teléfono del cliente',
    field: 'phone',
    render: () => (
      <span className='text-sm'>
        {shipping.recipientPhoneNumber || 'Sin teléfono'}
      </span>
    ),
  },
  {
    label: 'Dirección de entrega',
    field: 'delivery',
    render: () => {
      const hasCoordinates =
        shipping.destinationAddress?.lat && shipping.destinationAddress?.long;
      const addressText = `${getFullAddress(shipping.destinationAddress)} - ${shipping.destinationCity?.name || ''}`;

      return (
        <div className='flex flex-col'>
          {hasCoordinates ? (
            <button
              onClick={handleOpenDestinationMap}
              className='text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left'
            >
              {addressText}
            </button>
          ) : (
            <span className='text-sm'>{addressText}</span>
          )}
          {cleanShippingDetails(shipping.deliveryDetails) && (
            <span className='text-xs text-gray-600 mt-1'>
              {cleanShippingDetails(shipping.deliveryDetails)}
            </span>
          )}
        </div>
      );
    },
  },
  {
    label: 'Detalle de entrega',
    field: 'deliveryDetails',
    render: () => (
      <span className='text-sm'>
        {cleanShippingDetails(shipping.deliveryDetails) || 'N/A'}
      </span>
    ),
  },
  {
    label: 'Fecha de entrega',
    field: 'date',
    render: () => (
      <span className='text-sm'>{extractDate(shipping.dateToDelivery)}</span>
    ),
  },
  ...(shipping.pickupCycleTime
    ? [
        {
          label: `Hora de la vuelta (hora de ${COUNTRY_LABEL[COUNTRY]})`,
          field: 'pickupCycleTime',
          render: () => {
            const there = wallClockInCountry(
              shipping.pickupCycleTime as string,
              COUNTRY,
              OTHER_COUNTRY
            );
            return (
              <span className='text-sm'>
                {shipping.pickupCycleTime}
                {there ? (
                  <span className='text-gray-500'>
                    {' '}
                    ({there} en {COUNTRY_LABEL[OTHER_COUNTRY]})
                  </span>
                ) : null}
              </span>
            );
          },
        },
      ]
    : []),
  {
    label: 'Fecha de ingreso',
    field: 'entry',
    render: () => (
      <span className='text-sm'>{formatDate(shipping.createdAt)}</span>
    ),
  },
  {
    label: 'Canal',
    field: 'channel',
    render: () => (
      <span className='text-sm'>{shipping.channel?.name || 'N/A'}</span>
    ),
  },
];

export const getShippingDetailsSecondTableColumns = (
  shipping: ShippingApi,
  handleOpenOriginMap?: () => void,
  originAddressOverride?: string
): ColumnConfig[] => [
  {
    label: 'Estado',
    field: 'status',
    render: () => (
      <span className='text-sm'>
        {shipping.status?.commercialName || 'N/A'}
      </span>
    ),
  },
  {
    label: 'Etapa logística',
    field: 'shippingLevel',
    render: () => {
      const isPickup = isPickupServiceVisit(shipping.serviceWithOperator);
      const labels: Record<string, string> = {
        CREATED: 'Ingresado',
        WAS_PROCESSED: 'Procesado',
        HAS_AN_ATTEMPT: isPickup ? 'Intento de retiro' : 'Intento de entrega',
        IS_DELIVERED: isPickup ? 'Retirado' : 'Entregado',
      };
      const value = shipping.shippingLevel;
      return (
        <span className='text-sm'>{value ? (labels[value] ?? value) : 'N/A'}</span>
      );
    },
  },
  {
    label: 'Sub-operador logístico',
    field: 'subLogisticOperator',
    render: () => {
      if (!shipping.logisticOperator) {
        return <span className='text-sm'>N/A</span>;
      }

      const isSubOperator = shipping.logisticOperator.headquarter === false;

      if (isSubOperator) {
        return (
          <span className='text-sm'>{shipping.logisticOperator.name}</span>
        );
      }

      return <span className='text-sm'>N/A</span>;
    },
  },
  {
    label: 'Servicio',
    field: 'service',
    render: () => {
      const service = (
        shipping.commercialService?.name
          ? shipping.commercialService
          : shipping.serviceWithOperator?.commercialService
      ) as any;

      if (!service?.name) {
        return <span className='text-sm'>N/A</span>;
      }

      const name = service.commercialName || service.name;
      const color = service.color || '#FFB800';

      return (
        <div
          className='px-3 py-1 rounded-md text-sm font-semibold inline-block'
          style={{
            backgroundColor: `${color}20`,
            color: color,
          }}
        >
          {name}
        </div>
      );
    },
  },
  {
    label: 'Número de factura',
    field: 'invoiceNumber',
    render: () => (
      <span className='text-sm'>{shipping.invoiceNumber?.trim() || 'N/A'}</span>
    ),
  },
  {
    label: 'Referencia del comercio',
    field: 'commerceReference',
    render: () => (
      <span className='text-sm'>
        {shipping.commerceReference?.trim() || 'N/A'}
      </span>
    ),
  },
  {
    label: 'Sucursal',
    field: 'branch',
    render: () => (
      <span className='text-sm'>{shipping.branch?.name || 'N/A'}</span>
    ),
  },
  {
    label: 'Dirección de origen',
    field: 'origin',
    render: () => {
      const hasCoordinates =
        shipping.originAddress?.lat && shipping.originAddress?.long;
      const addressText =
        originAddressOverride ||
        [getFullAddress(shipping.originAddress), shipping.originCity?.name]
          .filter(Boolean)
          .join(' - ');

      return (
        <div className='flex flex-col'>
          {hasCoordinates && handleOpenOriginMap ? (
            <button
              onClick={handleOpenOriginMap}
              className='text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left'
            >
              {addressText}
            </button>
          ) : (
            <span className='text-sm'>{addressText}</span>
          )}
          {cleanShippingDetails(shipping.pickupDetails) && (
            <span className='text-xs text-gray-600 mt-1'>
              {cleanShippingDetails(shipping.pickupDetails)}
            </span>
          )}
        </div>
      );
    },
  },
  {
    label: 'Detalle de retiro',
    field: 'pickupDetails',
    render: () => (
      <span className='text-sm'>
        {cleanShippingDetails(shipping.pickupDetails) || 'N/A'}
      </span>
    ),
  },
  {
    label: 'Franja Horaria',
    field: 'timeSlot',
    render: () => (
      <span className='text-sm'>{getTimeSlotDisplayLabel(shipping)}</span>
    ),
  },
  {
    label: 'Zona de tarifa',
    field: 'zone',
    render: () => {
      const namesFromShippingZones = shipping.shippingZones
        ?.filter(isRateShippingZone)
        .map((shippingZone) => shippingZone.zone?.name)
        .filter(Boolean)
        .join(', ');
      return <span className='text-sm'>{namesFromShippingZones || 'N/A'}</span>;
    },
  },
  {
    label: 'Zona de ruteo',
    field: 'routingZone',
    render: () => {
      const namesFromShippingZones = shipping.shippingZones
        ?.filter(isRoutingShippingZone)
        .map((shippingZone) => shippingZone.zone?.name)
        .filter(Boolean)
        .join(', ');
      return <span className='text-sm'>{namesFromShippingZones || 'N/A'}</span>;
    },
  },
];

export const getShippingDetailsThirdTableColumns = (
  shipping: ShippingApi,
): ColumnConfig[] => [
  {
    label: 'Tamaño',
    field: 'size',
    render: () => (
      <span className='text-sm'>{shipping.size?.name || 'N/A'}</span>
    ),
  },
  {
    label: 'Último conductor',
    field: 'lastDriverName',
    render: () => (
      <span className='text-sm'>{shipping.lastDriverName || 'N/A'}</span>
    ),
  },
  {
    label: 'Ubicación',
    field: 'location',
    render: () => (
      <span className='text-sm'>{shipping.location?.name || '—'}</span>
    ),
  },
  {
    label: 'Etiqueta del correo',
    field: 'labelProvider',
    render: () => {
      if (!shipping.labelNumber) {
        if (isMissingCorreoLabelNumber(shipping)) {
          return (
            <span className='text-sm font-medium text-amber-600'>
              Sin n° de Correo
            </span>
          );
        }
        return <span className='text-sm'>N/A</span>;
      }
      return (
        <div className='flex flex-col'>
          <span className='text-sm font-medium'>{shipping.labelNumber}</span>
          {shipping.labelProvider && (
            <span className='text-xs text-gray-500'>{shipping.labelProvider}</span>
          )}
        </div>
      );
    },
  },
  {
    label: 'Geo',
    field: 'geocoderError',
    render: () => {
      if (!shipping.geocoderError) {
        return <span className='text-sm text-gray-400'>Sin errores</span>;
      }
      return (
        <div className='flex flex-col'>
          <span className='text-sm text-red-600 font-medium'>
            {shipping.geocoderErrorDescription ?? 'Error de geocodificación'}
          </span>
        </div>
      );
    },
  },
  ...(shipping.zoneError
    ? [
        {
          label: 'Error de zona',
          field: 'zoneError',
          render: () => (
            <div className='flex flex-col'>
              <span className='text-sm text-red-600 font-medium'>
                {shipping.zoneErrorDescription ?? 'Error de zonificación'}
              </span>
            </div>
          ),
        } as ColumnConfig,
      ]
    : []),
  {
    label: 'Categoría Sugerida',
    field: 'productCategoryAi',
    render: () => {
      const label = getAiCategoryLabel(shipping);
      if (label) {
        return <span className='text-sm'>{label}</span>;
      }
      if (shipping.productClassificationError) {
        return (
          <div className='flex flex-col'>
            <span className='text-xs font-semibold px-2 py-0.5 rounded bg-yellow-100 text-yellow-700 inline-block w-fit'>
              Sin categorizar
            </span>
            {shipping.productClassificationErrorDescription && (
              <span className='text-xs text-gray-500 mt-1'>
                {shipping.productClassificationErrorDescription}
              </span>
            )}
          </div>
        );
      }
      return <span className='text-sm text-gray-400'>—</span>;
    },
  },
  {
    label: 'Tamaño Sugerido',
    field: 'productSizeAi',
    render: () => {
      const label = getAiProductSizeLabel(shipping);
      return <span className='text-sm'>{label ?? '—'}</span>;
    },
  },
  {
    label: 'Riesgo',
    field: 'riskLevel',
    render: () => {
      const level = shipping.riskLevel;
      if (!level) return <span className='text-sm text-gray-400'>—</span>;
      const cfg = RISK_CONFIG[level];
      return (
        <div className='flex flex-col gap-1'>
          <span className={`text-sm font-bold px-3 py-1 rounded-md inline-block w-fit ${cfg.bg} ${cfg.text}`}>
            {cfg.label}
          </span>
          {shipping.riskExplanation && (
            <span className='text-xs text-gray-500 mt-1'>{shipping.riskExplanation}</span>
          )}
        </div>
      );
    },
  },
];
